/**
 * Live Video — coaching-staff variant (Zone 3, FR-VIDEO-01/06).
 *
 * A dedicated, video-first page (not a panel on Game Summary): the embed takes
 * the full content column and a persistent stat strip rides under it, updating
 * every ten seconds so an OC/DC on the sideline can glance from picture to
 * numbers without leaving the stream. Reachable from every core-app page via
 * the sidebar/tab navigation like any other view.
 *
 * The embed and the strip are independent on purpose: when the provider has no
 * stream for this event (or errors), the strip keeps updating — same fail-soft
 * contract the Fanatic variant honours.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useActiveEvent } from '../../app/active-event-context';
import { useApiClient } from '../../app/providers';
import { useSession } from '../../app/session-context';
import { PageHeader } from '../../components/page-header';
import { readEmbedFailure, VideoEmbed } from '../../components/video-embed';
import type { VideoEmbedFailReason } from '../../components/video-embed';
import { ApiError } from '../../lib/api-error';
import type { GameSummaryFeed, VideoEmbedInfo } from '../../lib/api-types';
import { APP_VIEWS } from '../../lib/constants';
import { formatStatValue } from '../summary/stat-block';

/** Strip cadence — matches the live surfaces' refresh budget. */
const STRIP_REFRESH_MS = 10_000;

const FAIL_COPY: Record<VideoEmbedFailReason, { title: string; body: string }> = {
  fanatic_restricted: {
    title: 'Video unavailable',
    body: 'The provider refused this stream for your role. The live stat strip below keeps updating.',
  },
  plan_excluded: {
    title: 'Video is not included in your plan',
    body: 'Live video embeds (Hudl, YouTube) are available from the Varsity plan. A Team Admin can upgrade under Billing. The live stat strip below keeps updating.',
  },
  not_configured: {
    title: 'No video provider configured',
    body: 'A Team Admin can connect one under Team Settings → Video. The live stat strip below keeps updating.',
  },
  not_available: {
    title: 'No video for this event yet',
    body: 'Attach a stream or recording to this event under Team Settings → Video. The live stat strip below keeps updating.',
  },
  error: {
    title: 'Video unavailable',
    body: 'The stream could not be loaded right now. The live stat strip below keeps updating.',
  },
};

type EmbedState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; embed: VideoEmbedInfo }
  | { kind: 'failed'; reason: VideoEmbedFailReason };

interface StripItem {
  label: string;
  value: string;
}

function possessionLabel(possession: string | null): string {
  if (possession === null) return '—';
  if (possession === 'offense') return 'Our ball';
  if (possession === 'defense') return 'Opponent ball';
  return 'Special teams';
}

function buildStrip(feed: GameSummaryFeed | null): StripItem[] {
  if (feed === null) {
    return [
      { label: 'Score', value: '—' },
      { label: 'Quarter', value: '—' },
      { label: 'Clock', value: '—' },
      { label: 'Possession', value: '—' },
    ];
  }
  const general = feed.statistics.general;
  const offense = feed.statistics.offense ?? {};
  const defense = feed.statistics.defense ?? {};
  return [
    {
      label: 'Score',
      value: `${general.total_score.team}–${general.total_score.opponent}`,
    },
    { label: 'Quarter', value: general.quarter > 0 ? `Q${general.quarter}` : '—' },
    { label: 'Clock', value: general.game_clock ?? '—' },
    { label: 'Possession', value: possessionLabel(general.possession) },
    { label: 'Passing yards', value: formatStatValue(offense.passing_yards, 'yards') },
    { label: 'Rushing yards', value: formatStatValue(offense.rushing_yards, 'yards') },
    { label: 'Completion %', value: formatStatValue(offense.completion_percent, 'percent') },
    { label: 'Tackles', value: formatStatValue(defense.total_tackles, 'count') },
  ];
}

export function LiveVideoPage(): JSX.Element {
  const apiClient = useApiClient();
  const { tenantSlug } = useSession();
  const { activeEvent, activeEventId } = useActiveEvent();
  const [embedState, setEmbedState] = useState<EmbedState>({ kind: 'idle' });
  const [feed, setFeed] = useState<GameSummaryFeed | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const base = `/app/${tenantSlug ?? ''}`;
  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  useEffect(() => {
    if (activeEventId === null) {
      setEmbedState({ kind: 'idle' });
      return undefined;
    }
    const controller = new AbortController();
    setEmbedState({ kind: 'loading' });
    void (async () => {
      try {
        const embed = await apiClient.getVideoEmbed(activeEventId, controller.signal);
        if (typeof embed?.embed_url !== 'string' || embed.embed_url === '') {
          setEmbedState({ kind: 'failed', reason: 'not_available' });
          return;
        }
        setEmbedState({ kind: 'ready', embed });
      } catch (cause) {
        if (controller.signal.aborted) return;
        setEmbedState({ kind: 'failed', reason: readEmbedFailure(cause) });
      }
    })();
    return () => controller.abort();
  }, [activeEventId, apiClient, reloadToken]);

  useEffect(() => {
    if (activeEventId === null) {
      setFeed(null);
      return undefined;
    }
    const controller = new AbortController();
    void (async () => {
      try {
        const loaded = await apiClient.getGameSummary(activeEventId, {
          unit: 'all',
          signal: controller.signal,
        });
        setFeed(loaded);
        setError(null);
      } catch (cause) {
        if (controller.signal.aborted) return;
        setError(
          ApiError.isApiError(cause) ? cause.message : 'The live stats could not be loaded.'
        );
      }
    })();
    return () => controller.abort();
  }, [activeEventId, apiClient, reloadToken]);

  useEffect(() => {
    if (activeEventId === null) return undefined;
    const timer = setInterval(reload, STRIP_REFRESH_MS);
    return () => clearInterval(timer);
  }, [activeEventId, reload]);

  if (activeEventId === null) {
    return (
      <div className="live-video" data-testid="live-video-page">
        <PageHeader className="live-video__head" title="Live Video" />
        <p className="muted">
          Pick an event in the switcher above to watch its stream, or open the{' '}
          <Link to={`${base}/${APP_VIEWS.events}`}>Event Manager</Link> to start one.
        </p>
      </div>
    );
  }

  const strip = buildStrip(feed);

  return (
    <div className="live-video" data-testid="live-video-page">
      <PageHeader
        className="live-video__head"
        title={activeEvent?.name ?? 'Live Video'}
        context="Live Video · stats refresh every 10 s"
        actions={
          <>
            <button className="btn btn--ghost btn--sm" onClick={reload} type="button">
              Refresh
            </button>
            <Link className="btn btn--secondary btn--sm" to={`${base}/${APP_VIEWS.gameSummary}`}>
              Game Summary
            </Link>
            <Link className="btn btn--ghost btn--sm" to={`${base}/${APP_VIEWS.statTracker}`}>
              Stat Tracker
            </Link>
          </>
        }
      />

      <div className="live-video__layout">
        <div className="live-video__stage" data-state={embedState.kind}>
          {embedState.kind === 'loading' || embedState.kind === 'idle' ? (
            <p className="muted" role="status">
              Loading video…
            </p>
          ) : null}

          {embedState.kind === 'ready' ? (
            <VideoEmbed
              embed={embedState.embed}
              eventId={activeEventId}
              title={`${activeEvent?.name ?? 'Game'} live video`}
            />
          ) : null}

          {embedState.kind === 'failed' ? (
            <div className="live-video__fallback" data-testid="video-unavailable" role="status">
              <p className="live-video__fallback-title">{FAIL_COPY[embedState.reason].title}</p>
              <p className="muted">{FAIL_COPY[embedState.reason].body}</p>
              {embedState.reason === 'error' ? (
                <button className="btn btn--secondary btn--sm" onClick={reload} type="button">
                  Try again
                </button>
              ) : null}
            </div>
          ) : null}
        </div>

        <aside className="live-video__side">
          <div
            aria-label="Live stat strip"
            className="live-video__strip"
            data-testid="live-stat-strip"
          >
            {strip.map((item) => (
              <div className="live-video__strip-item" key={item.label}>
                <span className="live-video__strip-label">{item.label}</span>
                <span className="live-video__strip-value">{item.value}</span>
              </div>
            ))}
          </div>
          {error !== null ? (
            <p className="banner banner--error" role="alert">
              {error}{' '}
              <button className="btn btn--ghost btn--sm" onClick={reload} type="button">
                Try again
              </button>
            </p>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
